import { useEffect, useState } from 'react';
import { DateInput } from '@/components/date-input';
import { ErrorBanner, Field, Textarea } from '@/components/form';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { todayIso } from '@/lib/dates';

/**
 * Void (document 05 ConfirmDialog): explains the ledger effect and asks for a date and an
 * audited reason. The caller runs the request.
 */
export function VoidDialog({
  open,
  title,
  description,
  minDate,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  title: string;
  description: string;
  minDate: string;
  pending: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: (date: string, reason: string) => void;
}) {
  const [date, setDate] = useState(todayIso());
  const [reason, setReason] = useState('');
  useEffect(() => {
    if (open) {
      setDate(todayIso() < minDate ? minDate : todayIso());
      setReason('');
    }
  }, [open, minDate]);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>
        {error ? <ErrorBanner>{error}</ErrorBanner> : null}
        <Field label="Void date" hint="The reversing entry is dated here.">
          {(id) => <DateInput id={id} value={date} onChange={setDate} />}
        </Field>
        <Field label="Reason" hint="Recorded in the audit log.">
          {(id) => (
            <Textarea
              id={id}
              value={reason}
              maxLength={500}
              onChange={(e) => setReason(e.target.value)}
            />
          )}
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="destructive"
            disabled={pending || reason.trim().length < 3}
            onClick={() => onConfirm(date, reason.trim())}
          >
            {pending ? 'Voiding…' : 'Void'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
